import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpUtilsService } from '../../commons/http-uilis.service';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';
import { Paging } from '../../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { IPD, IPDData, IPDDataArray } from '../../../model/ipd/ipd';

@Injectable({
    providedIn: 'root'
})
export class IPDService {
    private _url: string = `${environment.baseAPIURL}/settings`;
    constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) { }

    GETPaginatedIPDs(paging:Paging): Observable<IPD>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/ipds-model${this.buildQueryString(paging)}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: IPDDataArray = jsonConvert.deserializeObject(res, IPDDataArray);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    GETIPDById(ipdId:number): Observable<IPD>  | any{
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.get(`${this._url}/ipds-model/${ipdId}`,{ headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            const jsonConvert: JsonConvert = new JsonConvert();
                            const response: IPDData = jsonConvert.deserializeObject(res, IPDData);
                            if (response.success) {
                            	return response;
                            } else {
                            	this._httpUtils.handleError(response);
                                return;
                            }
                        }, catchError(err => {
                            if (err.status === 401) {
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }


    DELETEIPD(ipdId:number){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.delete(`${this._url}/ipds-model/${ipdId}`, { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }   

    POSTIPD(body:IPD){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.post(`${this._url}/ipds-model`, this.toCreatePayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    PUTIPD(ipdId:number, body:IPD){
        try {
            return this._httpUtils.getHTTPHeadersAsync().pipe(
                mergeMap(httpHeaders => {
                    return this._http.put(`${this._url}/ipds-model/${ipdId}`, this.toUpdatePayload(body), { headers: httpHeaders, withCredentials:true }).pipe(
                        map((res:any) => {
                            return res;
                        }, catchError(err => {
                            if (err.status === 401) { // Unauthorized
                                keycloakLogout({ redirectUri: window.location.origin });
                            }
                            throw err;
                        })));
                }), catchError(err => {
                    throw err;
                })
            );
        } catch (error) {
            throw error;
        }
    }

    private buildQueryString(paging: Paging): string {
        const params = new URLSearchParams({
            offset: String((paging.pageIndex - 1) * paging.pageSize),
            limit: String(paging.pageSize)
        });
        const sort = paging.sort.find((item) => item.value !== null);
        if (sort?.key) {
            params.set('sortBy', sort.key);
        }
        if (sort?.value === 'ascend') {
            params.set('sortOrder', 'asc');
        } else if (sort?.value === 'descend') {
            params.set('sortOrder', 'desc');
        }
        return `?${params.toString()}`;
    }

    private toCreatePayload(body: IPD) {
        return {
            name: body.name,
            note: body.note,
            components: (body.components ?? []).map((component) => ({
                code: component.code,
                value: Number(component.value) || 0
            }))
        };
    }

    private toUpdatePayload(body: IPD) {
        return {
            note: body.note,
            components: (body.components ?? []).map((component) => ({
                code: component.code,
                value: Number(component.value) || 0
            }))
        };
    }
}
